import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { StatWidget } from '../../components/custom/StatWidget';
import { 
  Users, Target, Building2, TrendingUp, 
  AlertCircle, Clock, ArrowRight, Loader2,
  Phone, Mail, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';

export const AdminDashboardPage = () => {
  const { club } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentClubs, setRecentClubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, clubsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/clubs'),
      ]);
      setStats(statsRes.data);
      setRecentClubs(clubsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
        {/* Admin Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 md:p-8 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-indigo-400" />
            <span className="text-indigo-400 font-heading uppercase tracking-wider text-sm">Panel de Administración</span>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
            Bienvenido, Solo Cracks
          </h1>
          <p className="mt-2 text-slate-300 max-w-xl">
            Gestiona todos los clubes, jugadoras y necesidades de la red desde aquí.
          </p>
        </div>

        {/* Global Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatWidget
            title="Total Jugadoras"
            value={stats?.total_players || 0}
            icon={Users}
            color="indigo"
          />
          <StatWidget
            title="Total Necesidades"
            value={stats?.total_needs || 0}
            icon={Target}
            color="amber"
          />
          <StatWidget
            title="Clubes Registrados"
            value={stats?.total_clubs || 0}
            icon={Building2}
            color="lime"
          />
          <StatWidget
            title="Necesidades Urgentes"
            value={stats?.needs_by_priority?.high || 0}
            icon={AlertCircle}
            color="red"
          />
        </div>

        {/* Position breakdown */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4">
              Jugadoras por Posición
            </h2>
            <div className="space-y-3">
              {[
                { key: 'GK', label: 'Porteras', color: 'bg-yellow-500' },
                { key: 'DEF', label: 'Defensas', color: 'bg-blue-500' },
                { key: 'MID', label: 'Mediocampistas', color: 'bg-green-500' },
                { key: 'FWD', label: 'Delanteras', color: 'bg-red-500' },
              ].map(pos => (
                <div key={pos.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${pos.color}`} />
                    <span className="text-slate-600">{pos.label}</span>
                  </div>
                  <span className="font-heading font-bold text-slate-800">
                    {stats?.players_by_position?.[pos.key] || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight mb-4">
              Necesidades por Prioridad
            </h2>
            <div className="space-y-3">
              {[
                { key: 'high', label: 'Alta Prioridad', color: 'bg-red-500' },
                { key: 'medium', label: 'Media Prioridad', color: 'bg-yellow-500' },
                { key: 'low', label: 'Baja Prioridad', color: 'bg-green-500' },
              ].map(pri => (
                <div key={pri.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${pri.color}`} />
                    <span className="text-slate-600">{pri.label}</span>
                  </div>
                  <span className="font-heading font-bold text-slate-800">
                    {stats?.needs_by_priority?.[pri.key] || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Link to="/admin/clubs" className="bg-white rounded-xl border border-slate-200 p-6 hover:border-indigo-300 transition-colors group">
            <Building2 className="w-8 h-8 text-indigo-500 mb-3" />
            <h3 className="font-heading font-bold text-lg text-slate-800 uppercase">Ver Clubes</h3>
            <p className="text-sm text-slate-500 mt-1">Todos los clubes con contactos</p>
            <ArrowRight className="w-5 h-5 text-indigo-500 mt-3 group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link to="/admin/players" className="bg-white rounded-xl border border-slate-200 p-6 hover:border-indigo-300 transition-colors group">
            <Users className="w-8 h-8 text-lime-500 mb-3" />
            <h3 className="font-heading font-bold text-lg text-slate-800 uppercase">Ver Jugadoras</h3>
            <p className="text-sm text-slate-500 mt-1">Todas las jugadoras con contactos</p>
            <ArrowRight className="w-5 h-5 text-indigo-500 mt-3 group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link to="/admin/needs" className="bg-white rounded-xl border border-slate-200 p-6 hover:border-indigo-300 transition-colors group">
            <Target className="w-8 h-8 text-amber-500 mb-3" />
            <h3 className="font-heading font-bold text-lg text-slate-800 uppercase">Ver Necesidades</h3>
            <p className="text-sm text-slate-500 mt-1">Todas las necesidades con contactos</p>
            <ArrowRight className="w-5 h-5 text-indigo-500 mt-3 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Recent Clubs */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-slate-800 uppercase tracking-tight">
              Clubes Recientes
            </h2>
            <Link to="/admin/clubs" className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1">
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentClubs.map((club) => (
              <div key={club.id} className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <span className="font-heading font-bold text-indigo-600">
                        {club.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{club.name}</h3>
                      <p className="text-sm text-slate-500">{club.league} • {club.country}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-500">{club.players_count} jugadoras</span>
                      <span className="text-slate-500">{club.needs_count} necesidades</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                      <Mail className="w-3 h-3" />
                      {club.contact_email}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboardPage;
